import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from './ui/button';
import api from '../lib/api';
import { useTableNavigation } from '../hooks/useTableNavigation';
import { useDraft } from '../hooks/useDraft';
import DraftStatusBadge from './DraftStatusBadge';

import { createEmptyRow, sortScannedRows, getDesignColors, getDesignSizes, getSizeBands, recomputeRowAmount, rowTotalQty, computeTotals, SIZE_ORDER } from './create-sales-entry/helpers';
import StockErrorBanner from './create-sales-entry/StockErrorBanner';
import ScannerCard from './create-sales-entry/ScannerCard';
import CustomerSection from './create-sales-entry/CustomerSection';
import InvoiceTable from './create-sales-entry/InvoiceTable';
import FreightFooter from './create-sales-entry/FreightFooter';
import SubstituteDialog from './create-sales-entry/SubstituteDialog';

const CreateSalesEntry = ({ user }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const prefillData = location.state?.prefillData || null;
  const editSE = location.state?.editSE || null;
  const fromSO = location.state?.fromSO || null;
  const isEditMode = !!editSE;

  // Master data
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [agents, setAgents] = useState([]);
  const [salesmen, setSalesmen] = useState([]);
  const [selectedSalesmanId, setSelectedSalesmanId] = useState('');
  const [distributors, setDistributors] = useState([]);
  const [brands, setBrands] = useState([]);

  // Form state
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerDropdownOpen, setCustomerDropdownOpen] = useState(false);
  const [entryType, setEntryType] = useState('direct_sale');
  const [billType, setBillType] = useState('invoice');
  const [linkedSOId, setLinkedSOId] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [selectedAgentId, setSelectedAgentId] = useState('');
  const [nextSeNumber, setNextSeNumber] = useState('');
  const [transportName, setTransportName] = useState('');
  const [orderSource, setOrderSource] = useState('direct');
  const [seNotes, setSeNotes] = useState('');
  const [customerWspType, setCustomerWspType] = useState('local');
  const [customerDiscount, setCustomerDiscount] = useState(0);
  const [applyDiscount, setApplyDiscount] = useState(null);

  // Distribution mode state
  const [channel, setChannelRaw] = useState('direct');
  const setChannel = (ch) => {
    setChannelRaw(ch);
    if (ch === 'via_distributor') {
      setRows([]);
      setNeedsCustPick(false);
      setCurrentDistCustId('');
      setCurrentDistCustName('');
    } else {
      setSelectedDistributor('');
      setDistributorSearch('');
      if (rows.length === 0) setRows([createEmptyRow(1)]);
    }
  };
  const [selectedDistributor, setSelectedDistributor] = useState('');
  const [distributorSearch, setDistributorSearch] = useState('');
  const [distributorDropdownOpen, setDistributorDropdownOpen] = useState(false);
  const [currentBoxNumber, setCurrentBoxNumber] = useState(1);
  const [currentDistCustId, setCurrentDistCustId] = useState('');
  const [currentDistCustName, setCurrentDistCustName] = useState('');
  const [distCustSearch, setDistCustSearch] = useState('');
  const [distCustDropdownOpen, setDistCustDropdownOpen] = useState(false);
  const [breakPopupIdx, setBreakPopupIdx] = useState(null);
  const [needsCustPick, setNeedsCustPick] = useState(false);

  // Table rows
  const [rows, setRows] = useState([createEmptyRow(1)]);
  const [saving, setSaving] = useState(false);
  const [freightCharges, setFreightCharges] = useState('');
  const [enumSizes, setEnumSizes] = useState([]);

  // Design substitute dialog
  const [substituteDialog, setSubstituteDialog] = useState(null);
  const [substituteSearch, setSubstituteSearch] = useState('');

  const customerRef = useRef(null);
  const distributorRef = useRef(null);
  const distCustRef = useRef(null);
  const handleTableKeyDown = useTableNavigation('se-invoice-table');

  // Draft auto-save (DB-backed, every 5 seconds)
  const { draftStatus, save: saveDraftData, clear: clearDraft } = useDraft('SE', { enabled: !isEditMode && !fromSO, initialDraftId: location.state?.draftId || null });

  useEffect(() => {
    if (isEditMode || fromSO || !selectedCustomer) return;
    const hasContent = rows.some(r => r.product_id);
    if (!hasContent) return;
    const custName = customerSearch.split(' (')[0] || '';
    const itemCount = rows.filter(r => r.product_id).length;
    saveDraftData(
      { rows, selectedCustomer, customerSearch, channel, entryType, billType, linkedSOId, invoiceNumber, transportName, orderSource, seNotes, freightCharges, selectedAgentId, selectedSalesmanId, selectedDistributor, distributorSearch },
      `${custName} — ${itemCount} item${itemCount !== 1 ? 's' : ''}`
    );
  }, [rows, selectedCustomer, channel, entryType, billType, invoiceNumber, transportName, seNotes, freightCharges]);

  // Refs for scanner access to distribution state
  const channelRef = useRef('direct');
  const currentBoxRef = useRef(1);
  const currentDistCustIdRef = useRef('');
  const currentDistCustNameRef = useRef('');
  useEffect(() => { channelRef.current = channel; }, [channel]);
  useEffect(() => { currentBoxRef.current = currentBoxNumber; }, [currentBoxNumber]);
  useEffect(() => { currentDistCustIdRef.current = currentDistCustId; }, [currentDistCustId]);
  useEffect(() => { currentDistCustNameRef.current = currentDistCustName; }, [currentDistCustName]);

  // Scanner
  const [scannerActive, setScannerActive] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const scannerRef = useRef(null);
  const fileInputRef = useRef(null);
  const lastPayloadRef = useRef(null);
  const scannerContainerId = 'se-qr-reader';
  const productsRef = useRef([]);
  const rowsRef = useRef(rows);
  const customerWspTypeRef = useRef('local');
  const customerDiscountRef = useRef(0);
  useEffect(() => { productsRef.current = products; }, [products]);
  useEffect(() => { rowsRef.current = rows; }, [rows]);
  useEffect(() => { customerWspTypeRef.current = customerWspType; }, [customerWspType]);
  useEffect(() => { customerDiscountRef.current = customerDiscount; }, [customerDiscount]);

  useEffect(() => {
    const handleClick = (e) => {
      if (customerRef.current && !customerRef.current.contains(e.target)) setCustomerDropdownOpen(false);
      if (distributorRef.current && !distributorRef.current.contains(e.target)) setDistributorDropdownOpen(false);
      if (distCustRef.current && !distCustRef.current.contains(e.target)) setDistCustDropdownOpen(false);
      if (!e.target.closest('[data-testid^="se-break"]')) setBreakPopupIdx(null);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => {
    const handleClick = () => setRows(prev => prev.map(r => ({ ...r, showDesignDropdown: false })));
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Scanner handlers
  const handleScannedCode = useCallback((code) => {
    const prods = productsRef.current;
    const curRows = rowsRef.current;
    let scanned = code.trim().toUpperCase();
    try { const qr = JSON.parse(code.trim()); if (qr.design) scanned = qr.design.toUpperCase(); } catch {}

    let product = prods.find(p => p.design_code?.toUpperCase() === scanned);
    let matchedSku = null;

    if (!product) {
      for (const p of prods) {
        const sku = (p.skus || []).find(s => s.sku_code?.toUpperCase() === scanned);
        if (sku) { product = p; matchedSku = sku; break; }
      }
    }

    if (!product && (scanned.includes('_') || scanned.includes('-'))) {
      const parts = scanned.split(/[_-]/);
      if (parts.length >= 2) {
        product = prods.find(p => p.design_code?.toUpperCase() === parts[0]);
      }
    }

    if (!product) { toast.error(`Design "${scanned}" not found`); return; }
    if (channelRef.current === 'via_distributor' && !currentDistCustIdRef.current) { toast.error('Select a customer first'); return; }

    const colors = getDesignColors(product);
    const distExtras = channelRef.current === 'via_distributor'
      ? { box_number: currentBoxRef.current, dist_customer_id: currentDistCustIdRef.current, dist_customer_name: currentDistCustNameRef.current }
      : { box_number: currentBoxRef.current };

    const wspType = customerWspTypeRef.current;
    const priceField = wspType === 'all' ? 'mrp' : 'wsp';
    const disc = customerDiscountRef.current || 0;

    let rowColor = '';
    let rowSize = '';
    let rowRate = 0;
    let rowSizeGroups = [];
    let rowIndividualSizes = [];
    let rowQty = 1;

    const buildSizeData = (color) => {
      const colorSkus = product.skus?.filter(s => s.color === color) || [];
      const byPrice = {};
      colorSkus.forEach(s => {
        const price = s[priceField] || s.wsp || 0;
        if (!byPrice[price]) byPrice[price] = [];
        byPrice[price].push(s.size);
      });
      const groups = Object.entries(byPrice)
        .filter(([, sizes]) => sizes.length > 1)
        .map(([price, sizes]) => ({ id: `set_${sizes[0]}_${sizes[sizes.length - 1]}`, label: `Set: ${sizes[0]}-${sizes[sizes.length - 1]}`, sizes, rate: parseFloat(price), count: sizes.length }));
      const indivs = colorSkus.map(s => ({ size: s.size, rate: s[priceField] || s.wsp || 0 }));
      return { groups, indivs };
    };

    if (matchedSku) {
      rowColor = matchedSku.color || '';
      rowSize = matchedSku.size || '';
      rowRate = matchedSku[priceField] || matchedSku.wsp || 0;
      const { groups, indivs } = buildSizeData(rowColor);
      rowSizeGroups = groups;
      rowIndividualSizes = indivs;
    } else if (colors.length === 1) {
      rowColor = colors[0];
      const { groups, indivs } = buildSizeData(rowColor);
      rowSizeGroups = groups;
      rowIndividualSizes = indivs;
      if (groups.length > 0) { rowSize = groups[0].id; rowRate = groups[0].rate; }
      else if (indivs.length > 0) { rowSize = indivs[0].size; rowRate = indivs[0].rate; }
    }

    const amount = disc > 0 ? Math.round(rowQty * rowRate * (1 - disc / 100)) : Math.round(rowQty * rowRate);

    const emptyIdx = curRows.findIndex(r => !r.product_id && r.box_number === distExtras.box_number && r.dist_customer_id === (distExtras.dist_customer_id || ''));
    setRows(prev => {
      const rowData = {
        product_id: product.id, design_code: product.design_code, designSearch: product.design_code,
        colors, product, showDesignDropdown: false,
        color: rowColor, size: rowSize, sizeGroups: rowSizeGroups, individualSizes: rowIndividualSizes,
        qty: rowQty, rate: rowRate, discount: disc, amount,
        ...distExtras,
      };
      if (emptyIdx >= 0) { const next = [...prev]; next[emptyIdx] = { ...next[emptyIdx], ...rowData }; return next; }
      return [...prev, { ...createEmptyRow(prev.length + 1, distExtras), ...rowData }];
    });
    const extra = rowColor ? ` | ${rowColor}${rowSize ? ' | ' + rowSize : ''}` : '';
    toast.success(`Scanned: ${product.design_code}${extra} @ ₹${rowRate}`);
  }, []);

  const startScanner = useCallback(async () => {
    setScannerActive(true);
    const { Html5Qrcode } = await import('html5-qrcode');
    setTimeout(() => {
      const el = document.getElementById(scannerContainerId);
      if (!el) { setScannerActive(false); return; }
      const scanner = new Html5Qrcode(scannerContainerId);
      scannerRef.current = scanner;
      scanner.start({ facingMode: "environment" }, { fps: 10, qrbox: { width: 250, height: 150 }, aspectRatio: 2.0 }, (t) => handleScannedCode(t), () => {}).catch(() => { toast.error('Camera not available. Use "Scan via Camera Photo" or wireless scanner.'); setScannerActive(false); });
    }, 200);
  }, [handleScannedCode]);

  const stopScanner = useCallback(() => {
    if (scannerRef.current) { try { scannerRef.current.stop().then(() => { try { scannerRef.current.clear(); } catch(e) {} }).catch(() => {}); } catch(e) {} scannerRef.current = null; }
    setScannerActive(false);
  }, []);

  const handleFileScan = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const { Html5Qrcode } = await import('html5-qrcode');
      const scanner = new Html5Qrcode("se-file-scanner-tmp");
      const result = await scanner.scanFileV2(file, true);
      handleScannedCode(result.decodedText);
      scanner.clear();
    } catch {
      toast.error('Could not read barcode from photo. Try again with a clearer image.');
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [handleScannedCode]);

  useEffect(() => { return () => { if (scannerRef.current) { try { scannerRef.current.stop().catch(() => {}); } catch(e) {} scannerRef.current = null; } }; }, []);

  // Auto-focus first design search input on page load
  useEffect(() => {
    if (dataLoaded) {
      setTimeout(() => {
        const input = document.querySelector('[data-testid="se-design-search-0"]');
        if (input) input.focus();
      }, 300);
    }
  }, [dataLoaded]);

  useEffect(() => {
    const load = async () => {
      try {
        const [custRes, prodRes, agentRes, distRes, brandRes, salesmenRes, enumsRes] = await Promise.all([
          api.get('/customers'), api.get('/products'), api.get('/agents'),
          api.get('/distributors'), api.get('/brands'),
          api.get('/targets/salesmen').catch(() => ({ data: [] })),
          api.get('/enums').catch(() => ({ data: {} })),
        ]);
        setCustomers(custRes.data.filter(c => c.is_active));
        setProducts(prodRes.data);
        setBrands(brandRes.data);
        setAgents(agentRes.data);
        setDistributors(distRes.data);
        window.__g360_distributors = distRes.data; // For prefillFromRTD access
        setSalesmen(salesmenRes.data || []);
        // Cache enum sizes for fallback in size popover
        const enumSizes = enumsRes.data?.sizes || [];
        if (enumSizes.length > 0) {
          try { localStorage.setItem('g360_sizes', JSON.stringify(enumSizes)); } catch {}
          setEnumSizes(enumSizes);
        }
        // Make sizes available immediately for prefill functions
        window.__g360_active_sizes = enumSizes;

        if (!editSE) {
          try {
            const numRes = await api.get('/sales-entries/next-number');
            setNextSeNumber(numRes.data.next_number);
          } catch {}
        } else {
          setNextSeNumber(editSE.entry_number || '');
        }

        if (prefillData) prefillFromRTD(custRes.data, prodRes.data, prefillData);
        else if (editSE) await prefillFromEdit(custRes.data, prodRes.data, distRes.data, editSE);
        else if (fromSO) {
          const rtdShape = {
            customer_id: fromSO.customer_id,
            entry_type: 'against_so',
            linked_so_id: fromSO.id,
            items: (fromSO.items || []).map(item => ({
              product_id: item.product_id,
              design_code: item.design_code,
              item_id: item.id,
              variants: (item.variants || [])
                .map(v => ({
                  size: v.size,
                  color: v.color,
                  qty: Math.max(0, (v.qty_ordered || 0) - (v.qty_dispatched || 0)),
                  wsp: v.wsp || 0,
                  linked_so_item_id: item.id,
                }))
                .filter(v => v.qty > 0),
            })).filter(i => i.variants.length > 0),
          };
          prefillFromRTD(custRes.data, prodRes.data, rtdShape);
        }
        if (!editSE && user?.role === 'salesman' && (salesmenRes.data || []).some(s => s.id === user.id)) {
          setSelectedSalesmanId(user.id);
        }
        setDataLoaded(true);
      } catch { toast.error('Failed to load data'); }
    };
    load();
  }, []);

  const prefillFromRTD = (custs, prods, data) => {
    const cust = custs.find(c => c.id === data.customer_id);
    if (cust) {
      setSelectedCustomer(cust.id);
      setCustomerSearch(cust.company_name + (cust.city ? ` (${cust.city})` : ''));
      setTransportName(cust.transport_preference || '');
      setCustomerWspType(cust.wsp_type || 'local');
      setCustomerDiscount(cust.discount_percent || 0);
      // Set channel, agent, salesman from customer defaults
      let ch = cust.default_channel || '';
      if (!ch) {
        if (cust.agent_id) ch = 'via_agent';
        else if (cust.distributor_id) ch = 'via_distributor';
        else ch = 'direct';
      }
      setChannelRaw(ch);
      if (cust.agent_id) setSelectedAgentId(cust.agent_id);
      if (ch === 'via_distributor' && cust.distributor_id) {
        const dist = (window.__g360_distributors || []).find(d => d.id === cust.distributor_id);
        if (dist) { setSelectedDistributor(dist.id); setDistributorSearch(dist.company_name); }
      }
    }
    setEntryType(data.entry_type || 'against_so');
    setLinkedSOId(data.linked_so_id || '');

    if (data.items?.length) {
      const wspType = cust?.wsp_type || 'local';
      const newRows = [];
      let sr = 1;
      for (const item of data.items) {
        const prod = prods.find(p => p.id === item.product_id);
        const colors = prod ? getDesignColors(prod) : [];
        // Group variants by color
        const colorMap = {};
        for (const v of (item.variants || [])) {
          if (!colorMap[v.color]) colorMap[v.color] = [];
          colorMap[v.color].push(v);
        }
        for (const [color, variants] of Object.entries(colorMap)) {
          const catalogSizes = prod ? getDesignSizes(prod, color, wspType, window.__g360_active_sizes || []) : [];
          const sizeQty = {};
          const sizeRate = {};
          for (const v of variants) {
            const qty = v.qty || v.can_dispatch || 0;
            if (qty > 0) {
              sizeQty[v.size] = qty;
              // Priority: variant wsp (from Process Items) > catalogue SKU > 0
              if (v.wsp && v.wsp > 0) {
                sizeRate[v.size] = v.wsp;
              } else {
                const sku = prod?.skus?.find(s => s.color === v.color && s.size === v.size);
                sizeRate[v.size] = sku ? (wspType === 'all' ? sku.mrp : sku.wsp) : 0;
              }
            }
          }
          // Fill rates from catalogue for sizes not in variants
          catalogSizes.forEach(s => { if (!(s.size in sizeRate)) sizeRate[s.size] = s.rate; });
          // Build individualSizes: merge catalogue sizes + variant sizes (for forwarding designs without SKUs)
          let indSizes = catalogSizes.length > 0 ? [...catalogSizes] : [];
          for (const sz of Object.keys(sizeQty)) {
            if (!indSizes.some(s => s.size === sz)) {
              indSizes.push({ size: sz, rate: sizeRate[sz] || 0 });
            }
          }
          // Ensure variant rates override catalogue rates in individualSizes
          indSizes = indSizes.map(s => ({ ...s, rate: sizeRate[s.size] ?? s.rate }));
          const row = {
            ...createEmptyRow(sr),
            product_id: item.product_id, design_code: item.design_code,
            designSearch: item.design_code, color, sizeQty, sizeRate,
            individualSizes: indSizes, bands: getSizeBands(indSizes),
            colors: colors.length > 0 ? colors : [...new Set(variants.map(v => v.color))],
            product: prod, showDesignDropdown: false,
            linked_so_id: item.linked_so_id || data.linked_so_id || '',
            linked_so_item_id: item.linked_so_item_id || item.item_id || '',
          };
          row.amount = recomputeRowAmount(row);
          newRows.push(row);
          sr++;
        }
      }
      if (newRows.length) setRows(newRows);
    }
  };

  const prefillFromEdit = async (custs, prods, dists, se) => {
    if (se.channel === 'via_distributor' && se.distributor_id) {
      const dist = dists.find(d => d.id === se.distributor_id);
      if (dist) {
        setSelectedDistributor(dist.id);
        setDistributorSearch(dist.company_name);
      }
      setChannelRaw('via_distributor');
    } else {
      const cust = custs.find(c => c.id === se.customer_id);
      if (cust) {
        setSelectedCustomer(cust.id);
        setCustomerSearch(cust.company_name + (cust.city ? ` (${cust.city})` : ''));
        setTransportName(se.transport_name || cust.transport_preference || '');
        setCustomerWspType(cust.wsp_type || 'local');
        setCustomerDiscount(cust.discount_percent || 0);
        setApplyDiscount(false);
      }
      setChannelRaw(se.channel || 'direct');
      if (se.agent_id) setSelectedAgentId(se.agent_id);
      else if (cust && cust.agent_id) setSelectedAgentId(cust.agent_id);
    }
    if (se.salesman_id) setSelectedSalesmanId(se.salesman_id);
    setEntryType(se.entry_type || 'direct_sale');
    setBillType(se.bill_type || 'invoice');
    setLinkedSOId(se.linked_so_id || '');
    setInvoiceNumber(se.invoice_number || '');
    setTransportName(se.transport_name || '');
    setOrderSource(se.order_source || 'direct');
    setSeNotes(se.notes || '');
    setFreightCharges(se.freight_charges || '');

    const cust = custs.find(c => c.id === se.customer_id);
    const wspType = cust?.wsp_type || 'local';

    // Ensure every product referenced by this SE is resolved (the /products list
    // is capped and excludes deleted designs) so colours/sizes populate on edit.
    const prodMap = {};
    for (const p of prods) prodMap[p.id] = p;
    const missingIds = [...new Set((se.items || []).map(i => i.product_id).filter(id => id && !prodMap[id]))];
    if (missingIds.length) {
      const fetched = await Promise.all(missingIds.map(id => api.get(`/products/${id}`).then(r => r.data).catch(() => null)));
      fetched.forEach(p => { if (p) prodMap[p.id] = p; });
    }

    const editRows = [];
    let sr = 1;
    for (const item of (se.items || [])) {
      const prod = prodMap[item.product_id];
      const variantColors = [...new Set((item.variants || []).map(v => v.color).filter(Boolean))];
      const colors = prod ? getDesignColors(prod) : variantColors;
      // Group variants by color → build sizeQty/sizeRate per color row
      const colorVariants = {};
      for (const v of (item.variants || [])) {
        const c = v.color || 'Other';
        if (!colorVariants[c]) colorVariants[c] = [];
        colorVariants[c].push(v);
      }
      for (const [color, variants] of Object.entries(colorVariants)) {
        const sizes = prod ? getDesignSizes(prod, color, wspType, window.__g360_active_sizes || []) : [];
        const sizeQty = {};
        const sizeRate = {};
        for (const v of variants) {
          const qty = v.qty || 0;
          if (qty > 0) {
            sizeQty[v.size] = (sizeQty[v.size] || 0) + qty;
            const sku = prod?.skus?.find(s => s.color === v.color && s.size === v.size);
            sizeRate[v.size] = v.wsp || (sku ? (wspType === 'all' ? sku.mrp : sku.wsp) : 0);
          }
        }
        // Fill rates for all available sizes
        sizes.forEach(s => { if (!(s.size in sizeRate)) sizeRate[s.size] = s.rate; });
        // Ensure saved sizes are in individualSizes
        let indSizes = sizes.length > 0 ? sizes : [];
        for (const sz of Object.keys(sizeQty)) {
          if (!indSizes.some(s => s.size === sz)) {
            indSizes = [...indSizes, { size: sz, rate: sizeRate[sz] || 0 }];
          }
        }
        const discount = variants[0]?.discount_percent || 0;
        const row = {
          ...createEmptyRow(sr, { box_number: item.box_number || 1, dist_customer_id: item.customer_id || '', dist_customer_name: item.customer_name || '' }),
          product_id: item.product_id, design_code: item.design_code,
          designSearch: item.design_code, color, sizeQty, sizeRate,
          individualSizes: indSizes, bands: getSizeBands(indSizes),
          discount, colors, product: prod, showDesignDropdown: false, productMissing: !prod,
          linked_so_id: variants[0]?.linked_so_id || item.linked_so_id || se.linked_so_id || '',
          linked_so_item_id: variants[0]?.linked_so_item_id || item.linked_so_item_id || '',
        };
        row.amount = recomputeRowAmount(row);
        editRows.push(row);
        sr++;
      }
    }
    if (editRows.length === 0) editRows.push(createEmptyRow(1));
    setRows(editRows);
  };

  const selectCustomer = (cust) => {
    setSelectedCustomer(cust.id);
    setCustomerSearch(cust.company_name + (cust.city ? ` (${cust.city})` : ''));
    setCustomerDropdownOpen(false);
    let ch = cust.default_channel || '';
    if (!ch) {
      if (cust.agent_id) ch = 'via_agent';
      else if (cust.distributor_id) ch = 'via_distributor';
      else ch = 'direct';
    }
    setChannelRaw(ch);
    setTransportName(cust.transport_preference || '');
    setCustomerWspType(cust.wsp_type || 'local');
    const disc = cust.discount_percent || 0;
    setCustomerDiscount(disc);
    if (disc > 0) setApplyDiscount(null);
    if (ch === 'via_distributor' && cust.distributor_id) {
      const dist = distributors.find(d => d.id === cust.distributor_id);
      if (dist) { setSelectedDistributor(dist.id); setDistributorSearch(dist.company_name); }
    }
    if (cust.agent_id) setSelectedAgentId(cust.agent_id);
  };

  const selectDistributor = (dist) => {
    setSelectedDistributor(dist.id);
    setDistributorSearch(dist.company_name);
    setDistributorDropdownOpen(false);
    setNeedsCustPick(true);
    setCurrentBoxNumber(1);
    setRows([]);
  };

  const selectDistCustomer = (cust) => {
    setCurrentDistCustId(cust.id);
    setCurrentDistCustName(cust.company_name);
    setDistCustSearch('');
    setDistCustDropdownOpen(false);
    setNeedsCustPick(false);
    setRows(prev => [...prev, createEmptyRow(prev.length + 1, { box_number: currentBoxNumber, dist_customer_id: cust.id, dist_customer_name: cust.company_name })]);
    toast.success(`Packing for ${cust.company_name} — Box ${currentBoxNumber}`);
  };

  const handleBreak = (choice) => {
    setBreakPopupIdx(null);
    if (choice === 'new_customer') {
      setNeedsCustPick(true);
    } else if (choice === 'new_box') {
      setCurrentBoxNumber(prev => prev + 1);
      if (channel === 'via_distributor') {
        setCurrentDistCustId('');
        setCurrentDistCustName('');
        setNeedsCustPick(true);
      }
      toast.success(`Starting Box ${currentBoxNumber + 1}`);
    }
  };

  const filteredDistributors = useMemo(() => {
    if (!distributorSearch.trim()) return distributors.slice(0, 20);
    const q = distributorSearch.trim().toLowerCase();
    return distributors.filter(d => d.company_name?.toLowerCase().includes(q)).slice(0, 20);
  }, [distributorSearch, distributors]);

  const filteredDistCusts = useMemo(() => {
    if (!distCustSearch.trim()) return [];
    const q = distCustSearch.trim().toLowerCase();
    return customers.filter(c => c.distributor_id === selectedDistributor && (c.company_name?.toLowerCase().includes(q) || c.city?.toLowerCase().includes(q))).slice(0, 15);
  }, [distCustSearch, customers, selectedDistributor]);

  const groupedBoxRows = useMemo(() => {
    const boxes = {};
    rows.forEach((r, idx) => {
      const bn = r.box_number || 1;
      if (!boxes[bn]) boxes[bn] = {};
      if (channel === 'via_distributor') {
        const cid = r.dist_customer_id || 'unknown';
        if (!boxes[bn][cid]) boxes[bn][cid] = { name: r.dist_customer_name || '?', rows: [] };
        boxes[bn][cid].rows.push({ ...r, _idx: idx });
      } else {
        if (!boxes[bn]['_direct']) boxes[bn]['_direct'] = { name: null, rows: [] };
        boxes[bn]['_direct'].rows.push({ ...r, _idx: idx });
      }
    });
    return boxes;
  }, [rows, channel]);

  const hasMultipleBoxes = useMemo(() => Object.keys(groupedBoxRows).length > 1, [groupedBoxRows]);

  useEffect(() => {
    if (isEditMode) return; // Don't override saved discounts when editing
    if (applyDiscount === true && customerDiscount > 0) {
      setRows(prev => prev.map(r => {
        const updated = { ...r, discount: customerDiscount };
        updated.amount = recomputeRowAmount(updated);
        return updated;
      }));
    } else if (applyDiscount === false) {
      setRows(prev => prev.map(r => {
        const updated = { ...r, discount: 0 };
        updated.amount = recomputeRowAmount(updated);
        return updated;
      }));
    }
  }, [applyDiscount]);

  const filteredCustomers = useMemo(() => {
    if (!customerSearch.trim()) return customers.slice(0, 20);
    const q = customerSearch.trim().toLowerCase();
    return customers.filter(c =>
      c.company_name?.toLowerCase().includes(q) || c.city?.toLowerCase().includes(q) || (/\d/.test(customerSearch) && c.phone?.includes(q))
    ).slice(0, 20);
  }, [customerSearch, customers]);

  // --- SO-style per-size selection handlers ---
  const standardSizes = useMemo(() => {
    const sizes = enumSizes.length > 0 ? enumSizes : (() => { try { return JSON.parse(localStorage.getItem('g360_sizes') || '[]'); } catch { return []; } })();
    const order = (s) => { const i = SIZE_ORDER.indexOf(s); return i === -1 ? 999 : i; };
    return [...sizes].sort((a, b) => order(a) - order(b));
  }, [enumSizes]);

  const getSizesForColor = useCallback((product, color) => getDesignSizes(product, color, customerWspType, standardSizes), [customerWspType, standardSizes]);

  const mutateRow = (idx, fn) => setRows(prev => {
    const next = [...prev];
    const row = { ...next[idx], sizeQty: { ...next[idx].sizeQty }, sizeRate: { ...next[idx].sizeRate } };
    fn(row);
    row.amount = recomputeRowAmount(row);
    next[idx] = row;
    return next;
  });

  const toggleSize = (idx, size) => mutateRow(idx, row => {
    if (size in row.sizeQty) { delete row.sizeQty[size]; }
    else { row.sizeQty[size] = parseInt(row.qty) || 1; }
  });

  const applyBand = (idx, band) => mutateRow(idx, row => {
    const masterQ = parseInt(row.qty) || 1;
    const allSel = band.sizes.every(s => s in row.sizeQty);
    if (allSel) band.sizes.forEach(s => { delete row.sizeQty[s]; });
    else band.sizes.forEach(s => { if (!(s in row.sizeQty)) row.sizeQty[s] = masterQ; });
  });

  const setSizeQtyHandler = (idx, size, val) => mutateRow(idx, row => { row.sizeQty[size] = val; });
  const setSizeRateHandler = (idx, size, val) => mutateRow(idx, row => { row.sizeRate[size] = val; });

  const selectAllSizes = (idx) => mutateRow(idx, row => {
    const masterQ = parseInt(row.qty) || 1;
    (row.individualSizes || []).forEach(s => { if (!(s.size in row.sizeQty)) row.sizeQty[s.size] = masterQ; });
  });

  const clearSizes = (idx) => mutateRow(idx, row => { row.sizeQty = {}; });

  const applyMultiColor = (idx, selections) => {
    setRows(prev => {
      if (!selections || selections.length === 0) return prev;
      const base = prev[idx];
      const built = selections.map(sel => {
        const sizes = getSizesForColor(base.product, sel.color);
        const sizeSet = new Map(sizes.map(s => [s.size, s.rate]));
        const sizeRate = {};
        Object.keys(sel.sizeQty).forEach(sz => {
          sizeRate[sz] = sizeSet.has(sz) ? sizeSet.get(sz) : 0;
          if (!sizeSet.has(sz)) sizeSet.set(sz, sizeRate[sz]);
        });
        const individualSizes = [...sizeSet.entries()].map(([size, rate]) => ({ size, rate }));
        const row = {
          ...createEmptyRow(0, { box_number: base.box_number, dist_customer_id: base.dist_customer_id, dist_customer_name: base.dist_customer_name }),
          product_id: base.product_id, design_code: base.design_code, designSearch: base.design_code,
          color: sel.color, individualSizes, bands: getSizeBands(individualSizes),
          sizeQty: { ...sel.sizeQty }, sizeRate, discount: base.discount || '',
          colors: base.colors, product: base.product, showDesignDropdown: false,
          linked_so_id: base.linked_so_id || '', linked_so_item_id: base.linked_so_item_id || '',
        };
        row.amount = recomputeRowAmount(row);
        return row;
      });
      let next = [...prev.slice(0, idx), ...built, ...prev.slice(idx + 1)];
      const lastBuiltIdx = idx + built.length - 1;
      if (lastBuiltIdx === next.length - 1) {
        const distExtras = channel === 'via_distributor'
          ? { box_number: base.box_number || 1, dist_customer_id: base.dist_customer_id || '', dist_customer_name: base.dist_customer_name || '' }
          : { box_number: base.box_number || 1 };
        next = [...next, createEmptyRow(0, distExtras)];
        const focusIdx = next.length - 1;
        setTimeout(() => document.querySelector(`[data-testid="se-design-search-${focusIdx}"]`)?.focus(), 130);
      }
      return next.map((r, i) => ({ ...r, sr: i + 1 }));
    });
  };

  const clearColorPicker = (idx) => setRows(prev => prev.map((r, i) => (i === idx && r.openColorPicker ? { ...r, openColorPicker: false } : r)));
  const clearSizePicker = (idx) => setRows(prev => prev.map((r, i) => (i === idx && r.openSizePicker ? { ...r, openSizePicker: false } : r)));

  const copyPrevSizes = (idx) => {
    if (idx <= 0) return;
    setRows(prev => {
      const cur = { ...prev[idx], sizeQty: { ...prev[idx].sizeQty }, sizeRate: { ...prev[idx].sizeRate } };
      if (Object.keys(cur.sizeQty).length > 0) return prev;
      const src = prev[idx - 1];
      const avail = new Set((cur.individualSizes || []).map(s => s.size));
      let copied = false;
      for (const [sz, q] of Object.entries(src.sizeQty || {})) {
        if (avail.has(sz)) { cur.sizeQty[sz] = q; copied = true; }
      }
      if (!copied) return prev;
      cur.qty = src.qty;
      cur.amount = recomputeRowAmount(cur);
      const next = [...prev]; next[idx] = cur;
      return next;
    });
  };

  const onSizesDone = (idx) => {
    setRows(prev => {
      const row = prev[idx];
      const hasSizes = Object.keys(row.sizeQty || {}).length > 0;
      const isLast = idx === prev.length - 1;
      if (hasSizes && isLast) {
        const nextIdx = prev.length;
        const distExtras = channel === 'via_distributor'
          ? { box_number: row.box_number || 1, dist_customer_id: row.dist_customer_id || '', dist_customer_name: row.dist_customer_name || '' }
          : { box_number: row.box_number || 1 };
        setTimeout(() => { document.querySelector(`[data-testid="se-design-search-${nextIdx}"]`)?.focus(); }, 120);
        return [...prev, createEmptyRow(prev.length + 1, distExtras)];
      }
      return prev;
    });
  };

  const updateRow = (idx, field, value) => {
    setRows(prev => {
      const next = [...prev];
      const row = { ...next[idx], sizeQty: { ...(next[idx].sizeQty || {}) }, sizeRate: { ...(next[idx].sizeRate || {}) } };
      row[field] = value;

      if (field === 'designSearch' && row.design_code && value !== row.design_code) {
        row.product_id = ''; row.design_code = ''; row.color = '';
        row.colors = []; row.product = null; row.individualSizes = []; row.bands = [];
        row.sizeQty = {}; row.sizeRate = {};
        row.rate = ''; row.qty = ''; row.discount = ''; row.amount = 0;
      }
      // When rate is changed via the uniform-rate input, update all selected sizeRates
      if (field === 'rate') {
        const selSizes = Object.keys(row.sizeQty);
        selSizes.forEach(s => { row.sizeRate[s] = value; });
        row.amount = recomputeRowAmount(row);
      }
      if (field === 'discount') {
        row.amount = recomputeRowAmount(row);
      }
      next[idx] = row;
      return next;
    });
  };

  const selectDesign = (idx, product) => {
    const colors = getDesignColors(product);
    setRows(prev => {
      const next = [...prev];
      const base = {
        ...next[idx], product_id: product.id, design_code: product.design_code,
        designSearch: product.design_code, colors, product, showDesignDropdown: false,
        rate: '', amount: 0, productMissing: false,
      };
      if (colors.length === 1) {
        const color = colors[0];
        const sizes = getSizesForColor(product, color);
        next[idx] = {
          ...base, color, individualSizes: sizes, bands: getSizeBands(sizes),
          sizeRate: Object.fromEntries(sizes.map(s => [s.size, s.rate])), sizeQty: {},
          openColorPicker: false, openSizePicker: true,
        };
      } else {
        next[idx] = {
          ...base, color: '', individualSizes: [], bands: [], sizeQty: {}, sizeRate: {},
          openColorPicker: colors.length > 1, openSizePicker: false,
        };
      }
      return next;
    });
    setSubstituteDialog({ rowIdx: idx, product });
    setSubstituteSearch('');
  };

  const confirmDesign = () => setSubstituteDialog(null);

  const selectSubstitute = (product) => {
    if (!substituteDialog) return;
    const idx = substituteDialog.rowIdx;
    const colors = getDesignColors(product);
    setRows(prev => {
      const next = [...prev];
      const base = {
        ...next[idx], product_id: product.id, design_code: product.design_code,
        designSearch: product.design_code, colors, product, color: '', rate: '', amount: 0,
        individualSizes: [], bands: [], sizeQty: {}, sizeRate: {},
        openColorPicker: colors.length > 1, openSizePicker: false,
      };
      next[idx] = base;
      return next;
    });
    setSubstituteDialog({ rowIdx: idx, product });
    setSubstituteSearch('');
  };

  const addRow = () => {
    const extras = channel === 'via_distributor'
      ? { box_number: currentBoxNumber, dist_customer_id: currentDistCustId, dist_customer_name: currentDistCustName }
      : { box_number: currentBoxNumber };
    setRows(prev => [...prev, createEmptyRow(prev.length + 1, extras)]);
  };
  const removeRow = (idx) => {
    if (rows.length <= 1) return;
    setRows(prev => prev.filter((_, i) => i !== idx).map((r, i) => ({ ...r, sr: i + 1 })));
  };

  const [bulkDiscount, setBulkDiscount] = useState('');
  const copyDiscountToAll = (val) => {
    const d = parseFloat(val) || 0;
    setRows(prev => prev.map(r => {
      const updated = { ...r, discount: d };
      updated.amount = recomputeRowAmount(updated);
      return updated;
    }));
  };

  const filteredDesigns = (search) => {
    if (!search.trim()) return products.slice(0, 15);
    const q = search.trim().toLowerCase();
    return products.filter(p => p.design_code.toLowerCase().includes(q) || p.category?.toLowerCase().includes(q)).slice(0, 15);
  };

  const [stockError, setStockError] = useState(null);

  const submitSE = async (forceOverride = false) => {
    const isDist = channel === 'via_distributor';
    if (!isDist && !selectedCustomer) { toast.error('Select a customer'); return; }
    if (isDist && !selectedDistributor) { toast.error('Select a distributor'); return; }
    if (!selectedSalesmanId) { toast.error('Select a Sales Person'); return; }
    const validRows = rows.filter(r => r.product_id && r.color && Object.keys(r.sizeQty || {}).length > 0);
    if (validRows.length === 0) { toast.error('Add at least one item with sizes selected'); return; }

    const itemsMap = {};
    for (const r of validRows) {
      const custKey = isDist ? (r.dist_customer_id || '') : '';
      const key = `${r.product_id}_${r.linked_so_item_id || 'direct'}_${custKey}`;
      if (!itemsMap[key]) {
        itemsMap[key] = {
          product_id: r.product_id, design_code: r.design_code,
          brand_id: r.product?.brand_id || null,
          linked_so_id: r.linked_so_id || linkedSOId || null,
          linked_so_item_id: r.linked_so_item_id || null,
          box_number: r.box_number || 1,
          variants: [],
          ...(isDist ? { customer_id: r.dist_customer_id, customer_name: r.dist_customer_name } : {}),
        };
      }
      const disc = parseFloat(r.discount) || 0;
      for (const [size, qty] of Object.entries(r.sizeQty || {})) {
        const q = parseInt(qty) || 0;
        if (q <= 0) continue;
        const rate = parseFloat((r.sizeRate || {})[size]) || 0;
        itemsMap[key].variants.push({
          size, color: r.color, qty: q, wsp: rate,
          discount_percent: disc,
          linked_so_id: r.linked_so_id || linkedSOId || null,
          linked_so_item_id: r.linked_so_item_id || null,
        });
      }
    }

    // Dedupe variants by size+color within each item
    for (const item of Object.values(itemsMap)) {
      const vMap = {};
      for (const v of item.variants) {
        const vKey = `${v.size}_${v.color}`;
        if (vMap[vKey]) { vMap[vKey].qty += v.qty; }
        else { vMap[vKey] = { ...v }; }
      }
      item.variants = Object.values(vMap);
    }

    const payload = {
      customer_id: isDist ? null : selectedCustomer,
      channel,
      distributor_id: isDist ? selectedDistributor : null,
      agent_id: channel === 'via_agent' ? selectedAgentId || null : null,
      salesman_id: selectedSalesmanId || null,
      entry_type: entryType,
      bill_type: billType,
      linked_so_id: linkedSOId || null,
      invoice_number: invoiceNumber || null,
      transport_name: transportName || null,
      order_source: orderSource || null,
      items: Object.values(itemsMap),
      notes: seNotes,
      freight_charges: parseFloat(freightCharges) || 0,
    };

    lastPayloadRef.current = payload;

    setSaving(true);
    setStockError(null);
    try {
      const forceParam = forceOverride ? '?force=true' : '';
      if (isEditMode) {
        const res = await api.put(`/sales-entries/${editSE.id}${forceParam}`, payload);
        toast.success(`SE ${res.data.entry_number} updated!`);
        clearDraft();
        navigate(`/sales-entry/${editSE.id}`);
      } else {
        const res = await api.post(`/sales-entries${forceParam}`, payload);
        toast.success(`SE ${res.data.entry_number} created!`);
        clearDraft();
        navigate('/sales-entry');
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : detail?.message || 'Failed to save entry');
    } finally { setSaving(false); }
  };

  const getBrandName = (id) => brands.find(b => b.id === id)?.name || '';
  const custObj = customers.find(c => c.id === selectedCustomer);
  const { grandTotal, grandQty } = computeTotals(rows);

  return (
    <div className="p-6 space-y-4 animate-fadeIn" data-testid="create-se-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Nunito' }}>{isEditMode ? `Edit ${editSE.entry_number}` : 'Create Sales Entry'}</h1>
          <p className="text-gray-500 text-sm">
            {isEditMode ? 'Modify entry details and items' : 'Invoice-style entry creation'}
            {nextSeNumber && <span className="ml-2 text-emerald-700 font-semibold" data-testid="next-se-number">SE #: {nextSeNumber}</span>}
            {!isEditMode && !fromSO && <DraftStatusBadge status={draftStatus} />}
          </p>
        </div>
        <div className="flex gap-2">
          {fromSO && (
            <Button variant="outline" className="border-blue-300 text-blue-700 hover:bg-blue-50" onClick={() => navigate(-1)} data-testid="se-back-to-process">
              ← Back to Process Items
            </Button>
          )}
          <Button variant="outline" onClick={() => navigate('/sales-entry')} data-testid="se-back-btn">Cancel</Button>
          <Button onClick={() => submitSE(false)} disabled={saving} className="bg-gradient-to-r from-emerald-600 to-teal-600" data-testid="se-save-btn">
            {saving ? 'Saving...' : isEditMode ? 'Update Entry' : 'Create Entry'}
          </Button>
        </div>
      </div>

      <StockErrorBanner
        stockError={stockError} setStockError={setStockError}
        submitSE={submitSE} lastPayloadRef={lastPayloadRef} navigate={navigate}
      />

      <CustomerSection
        channel={channel} setChannel={setChannel}
        customerRef={customerRef}
        customerSearch={customerSearch} setCustomerSearch={setCustomerSearch}
        customerDropdownOpen={customerDropdownOpen} setCustomerDropdownOpen={setCustomerDropdownOpen}
        filteredCustomers={filteredCustomers}
        selectCustomer={selectCustomer} setSelectedCustomer={setSelectedCustomer}
        selectedAgentId={selectedAgentId} setSelectedAgentId={setSelectedAgentId} agents={agents}
        selectedSalesmanId={selectedSalesmanId} setSelectedSalesmanId={setSelectedSalesmanId} salesmen={salesmen}
        billType={billType} setBillType={setBillType}
        invoiceNumber={invoiceNumber} setInvoiceNumber={setInvoiceNumber}
        transportName={transportName} setTransportName={setTransportName}
        seNotes={seNotes} setSeNotes={setSeNotes}
        custObj={custObj} customerDiscount={customerDiscount}
        applyDiscount={applyDiscount} setApplyDiscount={setApplyDiscount}
        distributorRef={distributorRef}
        distributorSearch={distributorSearch} setDistributorSearch={setDistributorSearch}
        distributorDropdownOpen={distributorDropdownOpen} setDistributorDropdownOpen={setDistributorDropdownOpen}
        filteredDistributors={filteredDistributors}
        selectDistributor={selectDistributor} setSelectedDistributor={setSelectedDistributor}
        selectedDistributor={selectedDistributor}
        currentBoxNumber={currentBoxNumber}
        currentDistCustId={currentDistCustId} currentDistCustName={currentDistCustName}
        needsCustPick={needsCustPick}
        distCustRef={distCustRef}
        distCustSearch={distCustSearch} setDistCustSearch={setDistCustSearch}
        distCustDropdownOpen={distCustDropdownOpen} setDistCustDropdownOpen={setDistCustDropdownOpen}
        filteredDistCusts={filteredDistCusts} selectDistCustomer={selectDistCustomer}
        rows={rows}
      />

      <ScannerCard
        scannerActive={scannerActive}
        startScanner={startScanner}
        stopScanner={stopScanner}
        fileInputRef={fileInputRef}
        handleFileScan={handleFileScan}
        scannerContainerId={scannerContainerId}
      />

      <InvoiceTable
        rows={rows} groupedBoxRows={groupedBoxRows} hasMultipleBoxes={hasMultipleBoxes} channel={channel}
        products={products} brands={brands}
        updateRow={updateRow} selectDesign={selectDesign} removeRow={removeRow} addRow={addRow}
        copyDiscountToAll={copyDiscountToAll}
        filteredDesigns={filteredDesigns} getBrandName={getBrandName} handleTableKeyDown={handleTableKeyDown}
        breakPopupIdx={breakPopupIdx} setBreakPopupIdx={setBreakPopupIdx} handleBreak={handleBreak}
        grandTotal={grandTotal} grandQty={grandQty}
        toggleSize={toggleSize} applyBand={applyBand} setSizeQty={setSizeQtyHandler} setSizeRate={setSizeRateHandler}
        selectAllSizes={selectAllSizes} clearSizes={clearSizes}
        getSizesForColor={getSizesForColor} applyMultiColor={applyMultiColor} clearColorPicker={clearColorPicker} clearSizePicker={clearSizePicker}
        setMasterQty={(i, v) => updateRow(i, 'qty', v)} onSizesDone={onSizesDone} copyPrevSizes={copyPrevSizes}
        bulkDiscount={bulkDiscount} setBulkDiscount={setBulkDiscount}
      />

      <FreightFooter
        freightCharges={freightCharges} setFreightCharges={setFreightCharges}
        grandTotal={grandTotal}
      />

      <SubstituteDialog
        substituteDialog={substituteDialog} confirmDesign={confirmDesign}
        substituteSearch={substituteSearch} setSubstituteSearch={setSubstituteSearch}
        filteredDesigns={filteredDesigns} selectSubstitute={selectSubstitute}
        getBrandName={getBrandName}
      />
    </div>
  );
};

export default CreateSalesEntry;
